<script setup>
import { ref } from 'vue'
import { useUserStore } from '../stores/userStore'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close'])
const userStore = useUserStore()

const newClient = ref({
  name: '',
  email: '',
  role: 'client',
  status: 'active',
  accessLevel: 'basic',
  subscription: {
    package: 'basic',
    billingCycle: 'monthly'
  },
  apiAccess: {
    enabled: false,
    rateLimit: 1000,
    endpoints: ['fixtures'],
    ipWhitelist: []
  },
  webConsole: {
    enabled: true,
    permissions: {
      viewFixtures: true,
      viewOdds: true,
      viewStats: false,
      manageUsers: false,
      manageSettings: false
    },
    twoFactorAuth: false,
    allowedIPs: [],
    lastLogin: null
  }
})

const consolePermissions = [
  { id: 'viewFixtures', label: 'View Fixtures', description: 'Access to view all fixtures and schedules' },
  { id: 'viewOdds', label: 'View Odds', description: 'Access to view odds and betting data' },
  { id: 'viewStats', label: 'View Statistics', description: 'Access to view detailed statistics and analytics' },
  { id: 'manageUsers', label: 'Manage Users', description: 'Ability to manage sub-users and permissions' },
  { id: 'manageSettings', label: 'Manage Settings', description: 'Access to account and system settings' }
]

const createClient = () => {
  userStore.addUser({
    id: Date.now(), // temporary ID generation
    ...newClient.value,
    apiKey: `ak_live_****${Math.random().toString(36).substr(2, 4)}`,
    lastActive: new Date().toISOString(),
    permissions: ['read'],
    quotaUsed: 0,
    quotaLimit: 50,
    createdAt: new Date().toISOString().split('T')[0],
    webConsole: {
      ...newClient.value.webConsole,
      lastLogin: null,
      sessionTimeout: 30, // minutes
      created: new Date().toISOString()
    }
  })
  emit('close')
  // Reset form
  newClient.value = {
    name: '',
    email: '',
    role: 'client',
    status: 'active',
    accessLevel: 'basic',
    subscription: {
      package: 'basic',
      billingCycle: 'monthly'
    },
    apiAccess: {
      enabled: false,
      rateLimit: 1000,
      endpoints: ['fixtures'],
      ipWhitelist: []
    },
    webConsole: {
      enabled: true,
      permissions: {
        viewFixtures: true,
        viewOdds: true,
        viewStats: false,
        manageUsers: false,
        manageSettings: false
      },
      twoFactorAuth: false,
      allowedIPs: [],
      lastLogin: null
    }
  }
}
</script>

<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg w-[800px] max-h-[90vh] overflow-y-auto">
      <div class="p-6 border-b flex justify-between items-center">
        <h2 class="text-xl font-semibold">Add New Client</h2>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="p-6">
        <form @submit.prevent="createClient">
          <!-- Basic Information -->
          <div class="space-y-6">
            <div class="grid grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input 
                  v-model="newClient.name"
                  type="text" 
                  required
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                  placeholder="Enter client name"
                >
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input 
                  v-model="newClient.email"
                  type="email" 
                  required
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                  placeholder="Enter email address"
                >
              </div>
            </div>

            <!-- Subscription Package -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Subscription Package</label>
              <div class="grid grid-cols-2 gap-4">
                <div 
                  v-for="pkg in userStore.packages" 
                  :key="pkg.id"
                  class="border rounded-lg p-4 cursor-pointer"
                  :class="newClient.subscription.package === pkg.id ? 'border-pink-500 bg-pink-50' : ''"
                  @click="newClient.subscription.package = pkg.id"
                >
                  <div class="flex justify-between items-center mb-2">
                    <h3 class="font-semibold">{{ pkg.name }}</h3>
                    <span class="text-lg font-bold">${{ pkg.price }}</span>
                  </div>
                  <ul class="text-sm text-gray-600 space-y-1">
                    <li v-for="feature in pkg.features" :key="feature" class="flex items-center">
                      <i class="fas fa-check text-green-500 mr-2"></i>
                      {{ feature }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- API Access -->
            <div>
              <div class="flex items-center justify-between mb-4">
                <label class="text-sm font-medium text-gray-700">API Access</label>
                <div class="relative inline-block w-12 h-6">
                  <input 
                    type="checkbox" 
                    v-model="newClient.apiAccess.enabled"
                    class="sr-only peer"
                  >
                  <div 
                    class="toggle-bg block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                           transition-colors duration-200 ease-in-out
                           peer-checked:bg-pink-500"
                  ></div>
                  <div 
                    class="toggle-dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow
                           transition-transform duration-200 ease-in-out
                           peer-checked:translate-x-6"
                  ></div>
                </div>
              </div>
              
              <div v-if="newClient.apiAccess.enabled" class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Rate Limit</label>
                  <input 
                    v-model="newClient.apiAccess.rateLimit"
                    type="number" 
                    class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                    placeholder="Requests per minute"
                  >
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">IP Whitelist</label>
                  <input 
                    v-model="newClient.apiAccess.ipWhitelist"
                    type="text" 
                    class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                    placeholder="Comma-separated IP addresses"
                  >
                </div>
              </div>
            </div>

            <!-- Web Console Access -->
            <div class="space-y-6">
              <div>
                <div class="flex items-center justify-between mb-4">
                  <div>
                    <label class="text-sm font-medium text-gray-700">Web Console Access</label>
                    <p class="text-sm text-gray-500">Enable access to the web management console</p>
                  </div>
                  <div class="relative inline-block w-12 h-6">
                    <input 
                      type="checkbox" 
                      v-model="newClient.webConsole.enabled"
                      class="sr-only peer"
                    >
                    <div 
                      class="toggle-bg block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                             transition-colors duration-200 ease-in-out
                             peer-checked:bg-pink-500"
                    ></div>
                    <div 
                      class="toggle-dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow
                             transition-transform duration-200 ease-in-out
                             peer-checked:translate-x-6"
                    ></div>
                  </div>
                </div>

                <div v-if="newClient.webConsole.enabled" class="space-y-6">
                  <!-- Console Permissions -->
                  <div class="bg-gray-50 rounded-lg p-4">
                    <h4 class="font-medium mb-3">Console Permissions</h4>
                    <div class="space-y-3">
                      <div 
                        v-for="perm in consolePermissions" 
                        :key="perm.id"
                        class="flex items-start space-x-3 p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <div class="relative inline-block w-12 h-6 flex-shrink-0">
                          <input 
                            type="checkbox" 
                            v-model="newClient.webConsole.permissions[perm.id]"
                            class="sr-only peer"
                          >
                          <div 
                            class="toggle-bg block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                                   transition-colors duration-200 ease-in-out
                                   peer-checked:bg-pink-500"
                          ></div>
                          <div 
                            class="toggle-dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow
                                   transition-transform duration-200 ease-in-out
                                   peer-checked:translate-x-6"
                          ></div>
                        </div>
                        <div class="flex-1">
                          <label class="font-medium text-sm">{{ perm.label }}</label>
                          <p class="text-sm text-gray-500">{{ perm.description }}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Security Settings -->
                  <div class="bg-gray-50 rounded-lg p-4">
                    <h4 class="font-medium mb-3">Security Settings</h4>
                    <div class="space-y-4">
                      <!-- 2FA Toggle -->
                      <div class="flex items-center justify-between">
                        <div>
                          <label class="font-medium text-sm">Two-Factor Authentication</label>
                          <p class="text-sm text-gray-500">Require 2FA for console login</p>
                        </div>
                        <div class="relative inline-block w-12 h-6">
                          <input 
                            type="checkbox" 
                            v-model="newClient.webConsole.twoFactorAuth"
                            class="sr-only peer"
                          >
                          <div 
                            class="toggle-bg block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                                   transition-colors duration-200 ease-in-out
                                   peer-checked:bg-pink-500"
                          ></div>
                          <div 
                            class="toggle-dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow
                                   transition-transform duration-200 ease-in-out
                                   peer-checked:translate-x-6"
                          ></div>
                        </div>
                      </div>

                      <!-- IP Restrictions -->
                      <div>
                        <label class="block text-sm font-medium mb-1">IP Restrictions</label>
                        <p class="text-sm text-gray-500 mb-2">Restrict console access to specific IP addresses</p>
                        <input 
                          type="text"
                          v-model="newClient.webConsole.allowedIPs"
                          class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                          placeholder="Enter comma-separated IP addresses"
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div class="p-6 border-t bg-gray-50 flex justify-end gap-3">
        <button 
          @click="$emit('close')"
          class="px-4 py-2 border rounded-lg hover:bg-gray-100"
        >
          Cancel
        </button>
        <button 
          @click="createClient"
          class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
        >
          Create Client
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Use the same toggle styles as UserRoleModal */
.toggle-bg {
  position: relative;
}

/* ... rest of the toggle styles ... */
</style> 